import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabaseGet, supabasePost } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { motion } from 'framer-motion';
import { 
  ChevronLeft,
  Clock,
  Euro,
  Calendar,
  User,
  Image as ImageIcon,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Send,
  AlertTriangle,
  CreditCard,
  Truck,
  Paperclip,
  X,
  FileText,
  Upload,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default timeline options (fallback)
const DEFAULT_TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', sort_order: 1 },
  { value: '2weeks', label: '2 Settimane', sort_order: 2 },
  { value: '1month', label: '1 Mese', sort_order: 3 },
  { value: '2months', label: '2 Mesi', sort_order: 4 },
  { value: '3months', label: '3 Mesi', sort_order: 5 },
  { value: '6months', label: '6 Mesi', sort_order: 6 },
  { value: 'flexible', label: 'Flessibile', sort_order: 7 },
];

export default function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [request, setRequest] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Proposal form state
  const [proposalMessage, setProposalMessage] = useState('');
  const [proposalPrice, setProposalPrice] = useState('');
  const [proposalTimeline, setProposalTimeline] = useState('');
  const [proposalShippingCost, setProposalShippingCost] = useState('');
  const [proposalFreeShipping, setProposalFreeShipping] = useState(false);
  const [submittingProposal, setSubmittingProposal] = useState(false);
  const [proposalSent, setProposalSent] = useState(false);
  
  // Attachments state
  const [proposalAttachments, setProposalAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  // Allowed file types
  const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'pdf'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_ATTACHMENTS = 5;
  
  // Timeline options from DB
  const [timelineOptions, setTimelineOptions] = useState(DEFAULT_TIMELINE_OPTIONS);
  
  // Stripe status for crafters
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);

  // Check Stripe status for crafters
  const checkStripeStatus = async () => {
    const isCrafterUser = profile?.role === 'crafter' || profile?.is_crafter_enabled;
    if (!user || !isCrafterUser) {
      setStripeLoading(false);
      return;
    }
    
    setStripeLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/connect/account-status/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setStripeConnected(data.has_account && data.charges_enabled);
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    } finally {
      setStripeLoading(false);
    }
  };

  // Fetch timeline options from DB
  useEffect(() => {
    const fetchTimelineOptions = async () => {
      try {
        const response = await fetch(`${API_URL}/api/request-options/public`);
        if (response.ok) {
          const data = await response.json();
          if (data.timeline_options?.length > 0) {
            setTimelineOptions(data.timeline_options);
          }
        }
      } catch (error) {
        console.error('Error fetching timeline options:', error);
      }
    };
    fetchTimelineOptions();
  }, []);

  useEffect(() => {
    if (id) {
      loadRequestData();
    }
  }, [id]);

  // Check Stripe status when user/profile loads (for crafters)
  useEffect(() => {
    if (user && profile) {
      checkStripeStatus();
    }
  }, [user, profile]);

  // Pre-select client's requested timeline when request loads
  useEffect(() => {
    if (request?.timeline && timelineOptions.length > 0) {
      const clientTimeline = request.timeline;
      const isValidTimeline = timelineOptions.some(o => o.value === clientTimeline);
      setProposalTimeline(isValidTimeline ? clientTimeline : timelineOptions[0]?.value || '1month');
    }
  }, [request, timelineOptions]);

  // Helper: Extract max budget from price_range
  const getMaxBudgetFromRange = (priceRange) => {
    if (!priceRange) return null;
    if (priceRange.startsWith('custom:')) {
      return parseFloat(priceRange.split(':')[1]);
    }
    if (priceRange.includes('-')) {
      const [, max] = priceRange.split('-');
      return parseFloat(max);
    }
    if (priceRange.endsWith('+')) {
      return parseFloat(priceRange.replace('+', ''));
    }
    return null;
  };

  // Helper: Get timeline sort order for comparison
  const getTimelineSortOrder = (timelineValue) => {
    if (!timelineValue) return 999;
    if (timelineValue.startsWith('custom:')) return 999;
    const found = timelineOptions.find(o => o.value === timelineValue);
    return found?.sort_order || 999;
  };

  // Check if proposed price exceeds client budget
  const isPriceOverBudget = () => {
    if (!request?.price_range || !proposalPrice) return false;
    const maxBudget = getMaxBudgetFromRange(request.price_range);
    if (maxBudget === null) return false;
    return parseFloat(proposalPrice) > maxBudget;
  };

  // Check if proposed timeline exceeds client request
  const isTimelineOverRequested = () => {
    if (!request?.timeline || !proposalTimeline) return false;
    const requestedOrder = getTimelineSortOrder(request.timeline);
    const proposedOrder = getTimelineSortOrder(proposalTimeline);
    return proposedOrder > requestedOrder;
  };

  // Get suggested price placeholder based on client budget
  const getPricePlaceholder = () => {
    if (!request?.price_range) return 'Es: 350';
    const maxBudget = getMaxBudgetFromRange(request.price_range);
    if (maxBudget) {
      return `Max budget cliente: €${maxBudget}`;
    }
    return 'Es: 350';
  };

  const loadRequestData = async () => {
    setLoading(true);
    try {
      // Load request
      const requestData = await supabaseGet(
        'direct_requests',
        `id=eq.${id}&select=*`,
        false
      );
      
      if (Array.isArray(requestData) && requestData.length > 0) {
        setRequest(requestData[0]);
        
        // Load client profile
        const clientData = await supabaseGet(
          'profiles',
          `id=eq.${requestData[0].client_id}&select=*`,
          false
        );
        
        if (Array.isArray(clientData) && clientData.length > 0) {
          setClient(clientData[0]);
        }

        // Check if user already sent a proposal
        if (user) {
          const proposalData = await supabaseGet(
            'proposals',
            `request_id=eq.${id}&crafter_id=eq.${user.id}&select=id`,
            false
          );
          if (Array.isArray(proposalData) && proposalData.length > 0) {
            setProposalSent(true);
          }
        }
      }
    } catch (error) {
      console.error('Error loading request:', error);
      toast.error('Errore nel caricamento della richiesta');
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload for attachments
  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Check max attachments
    if (proposalAttachments.length + files.length > MAX_ATTACHMENTS) {
      toast.error(`Puoi allegare massimo ${MAX_ATTACHMENTS} file`);
      return;
    }
    
    for (const file of files) {
      // Validate extension
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`Formato non consentito: ${file.name}. Usa JPG, PNG, WEBP o PDF`);
        continue;
      }
      
      // Validate size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File troppo grande: ${file.name}. Max 5MB`);
        continue;
      }
      
      // Upload file
      setUploadingAttachment(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/api/upload/proposal-attachment`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.detail || 'Upload fallito');
        }
        
        const result = await response.json();
        setProposalAttachments(prev => [...prev, {
          url: result.url,
          filename: result.filename,
          type: result.type,
          originalName: result.original_name || file.name
        }]);
        
        toast.success(`${file.name} caricato`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.message || `Errore nel caricamento di ${file.name}`);
      } finally {
        setUploadingAttachment(false);
      }
    }
    
    // Reset input
    e.target.value = '';
  };
  
  // Remove attachment
  const handleRemoveAttachment = (index) => {
    setProposalAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitProposal = async () => {
    if (!user) {
      toast.error('Devi essere autenticato');
      navigate('/login');
      return;
    }

    if (!proposalMessage.trim()) {
      toast.error('Scrivi un messaggio per il cliente');
      return;
    }

    if (!proposalPrice || parseFloat(proposalPrice) <= 0) {
      toast.error('Inserisci un prezzo valido');
      return;
    }

    setSubmittingProposal(true);
    try {
      const proposalData = {
        request_id: request.id,
        crafter_id: user.id,
        message: proposalMessage,
        price: parseFloat(proposalPrice),
        timeline: proposalTimeline || 'Da concordare',
        attachments: proposalAttachments.map(a => a.url),
        shipping_cost: proposalFreeShipping ? 0 : (parseFloat(proposalShippingCost) || 0)
      };

      const response = await fetch(`${API_URL}/api/proposals/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(proposalData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      // Send message notification to the client
      try {
        await fetch(`${API_URL}/api/notifications/proposal-message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: request.client_id,
            crafter_id: user.id,
            crafter_name: profile?.full_name || 'Un artigiano',
            request_id: request.id,
            request_title: request.title,
            frontend_url: window.location.origin
          })
        });
      } catch (notifError) {
        console.error('Error sending notification message:', notifError);
        // Don't fail the proposal if notification fails
      }

      toast.success('Proposta inviata con successo!');
      setProposalSent(true);
    } catch (error) {
      console.error('Proposal error:', error);
      toast.error(error.message || 'Errore durante l\'invio della proposta');
    } finally {
      setSubmittingProposal(false);
    }
  };

  const getPriceLabel = (range) => {
    if (!range) return '';
    if (range.startsWith('custom:')) {
      return `€${range.split(':')[1]}`;
    }
    switch (range) {
      case '100-500': return '€100 - €500';
      case '500-2000': return '€500 - €2.000';
      case 'custom': return 'Personalizzato';
      default: return range;
    }
  };

  const getTimelineLabel = (tl) => {
    switch (tl) {
      case '1-2days': return '1-2 giorni';
      case '3-5days': return '3-5 giorni';
      case '1week': return '1 settimana';
      case '1-2weeks': return '1-2 settimane';
      case '3-4weeks': return '3-4 settimane';
      case '1month': return '1 mese';
      case 'over-month': return 'Oltre 1 mese';
      case 'urgent': return 'Urgente';
      default: return tl;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#008080]"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Richiesta non trovata</h2>
          <Link to="/requests">
            <Button className="bg-[#008080] text-white">Torna alle Richieste</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isCrafter = profile?.role === 'crafter' || profile?.is_crafter_enabled;
  const isOwnRequest = user?.id === request.client_id;

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="request-detail-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <Link to="/requests" className="inline-flex items-center text-slate-600 hover:text-[#008080] mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Torna alle Richieste
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Request Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <Badge variant="outline" className="mb-2">{request.category}</Badge>
                    <h1 className="text-3xl font-bold text-slate-800">{request.title}</h1>
                  </div>
                  <Badge 
                    className={request.status === 'open' ? 'bg-[#00CED1] text-white' : 'bg-slate-500 text-white'}
                  >
                    {request.status === 'open' ? 'Aperta' : request.status}
                  </Badge>
                </div>

                {/* Client Info */}
                {client && (
                  <div className="flex items-center gap-3 mb-6 pb-6 border-b">
                    <div className="w-12 h-12 rounded-full bg-[#008080] flex items-center justify-center text-white font-bold">
                      {client.avatar_url ? (
                        <img src={client.avatar_url} alt={client.full_name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        client.full_name?.[0]?.toUpperCase() || 'C'
                      )}
                    </div>
                    <div>
                      <p className="font-semibold">{client.full_name || 'Cliente'}</p>
                      <p className="text-sm text-slate-500">
                        Pubblicata il {new Date(request.created_at).toLocaleDateString('it-IT', { 
                          day: 'numeric', month: 'long', year: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {/* Description */}
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">Descrizione</h3>
                  <p className="text-slate-600 whitespace-pre-line">{request.description}</p>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <Euro className="w-4 h-4" />
                      <span className="text-sm">Budget</span>
                    </div>
                    <p className="font-bold text-[#008080] text-lg">{getPriceLabel(request.price_range)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">Tempi</span>
                    </div>
                    <p className="font-bold text-slate-800 text-lg">{getTimelineLabel(request.timeline)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">Scadenza</span>
                    </div>
                    <p className="font-bold text-slate-800 text-lg">
                      {request.timeline === 'urgent' ? 'Urgente' : 'Flessibile'}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Attachments */}
            {request.attachments && request.attachments.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Immagini di Riferimento ({request.attachments.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {request.attachments.map((url, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(url)}
                        className="aspect-square rounded-lg overflow-hidden bg-slate-100 hover:opacity-90 transition-opacity"
                      >
                        <img src={url} alt={`Riferimento ${index + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Image Lightbox */}
            {selectedImage && (
              <div 
                className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                onClick={() => setSelectedImage(null)}
              >
                <img 
                  src={selectedImage} 
                  alt="Immagine ingrandita" 
                  className="max-w-full max-h-full rounded-lg"
                />
              </div>
            )}
          </div>

          {/* Sidebar - Proposal Form */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="sticky top-24"
            >
              {isCrafter && !isOwnRequest ? (
                <Card className="p-6 border-2 border-[#DAA520]/30 bg-gradient-to-br from-[#DAA520]/5 to-transparent">
                  {/* Check Stripe connection first */}
                  {!stripeLoading && !stripeConnected ? (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                        <CreditCard className="w-8 h-8 text-amber-600" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-800 mb-2">Collega Stripe</h3>
                      <p className="text-slate-600 mb-4 text-sm">
                        Per inviare proposte e ricevere pagamenti, devi prima collegare il tuo account Stripe.
                      </p>
                      <Button 
                        onClick={() => navigate('/dashboard?tab=orders')}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="connect-stripe-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Configura Stripe
                      </Button>
                      <p className="text-xs text-slate-500 mt-3">
                        Sarai reindirizzato alla tua dashboard per completare la configurazione
                      </p>
                    </div>
                  ) : proposalSent ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-800 mb-2">Proposta Inviata!</h3>
                      <p className="text-slate-600 mb-4">
                        Il cliente riceverà la tua proposta e ti contatterà se interessato.
                      </p>
                      <Link to="/dashboard">
                        <Button variant="outline" className="w-full">
                          Vai alla Dashboard
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-5 h-5 text-[#DAA520]" />
                        <h3 className="text-xl font-bold">Invia la tua Proposta</h3>
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-6">
                        Presenta la tua offerta a questo cliente
                      </p>

                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="message">Il tuo messaggio *</Label>
                          <Textarea
                            id="message"
                            value={proposalMessage}
                            onChange={(e) => setProposalMessage(e.target.value)}
                            placeholder="Presentati, descrivi come realizzeresti questa creazione..."
                            rows={5}
                            className="mt-1"
                            data-testid="detail-proposal-message"
                          />
                        </div>

                        <div>
                          <Label htmlFor="price">Il tuo prezzo (€) *</Label>
                          <Input
                            id="price"
                            type="number"
                            value={proposalPrice}
                            onChange={(e) => setProposalPrice(e.target.value)}
                            placeholder={getPricePlaceholder()}
                            className={`mt-1 ${isPriceOverBudget() ? 'border-amber-500 focus:border-amber-500' : ''}`}
                            data-testid="detail-proposal-price"
                          />
                          {isPriceOverBudget() && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Il prezzo supera il budget del cliente. La proposta verrà comunque inviata.
                            </p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="timeline">Tempo stimato *</Label>
                          <Select value={proposalTimeline} onValueChange={setProposalTimeline}>
                            <SelectTrigger className={`mt-1 ${isTimelineOverRequested() ? 'border-amber-500' : ''}`} data-testid="detail-proposal-timeline">
                              <SelectValue placeholder="Seleziona tempo stimato" />
                            </SelectTrigger>
                            <SelectContent>
                              {timelineOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {isTimelineOverRequested() && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              I tempi superano la richiesta del cliente. La proposta verrà comunque inviata.
                            </p>
                          )}
                        </div>

                        {/* Shipping Cost */}
                        <div className="border border-slate-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-medium">Spese di spedizione</Label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={proposalFreeShipping}
                                onChange={(e) => {
                                  setProposalFreeShipping(e.target.checked);
                                  if (e.target.checked) setProposalShippingCost('');
                                }}
                                className="w-4 h-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                              />
                              <span className="text-xs text-slate-600">Spedizione gratuita</span>
                            </label>
                          </div>
                          {!proposalFreeShipping && (
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={proposalShippingCost}
                                onChange={(e) => setProposalShippingCost(e.target.value)}
                                placeholder="0.00"
                                className="pl-7"
                              />
                            </div>
                          )}
                          {proposalFreeShipping && (
                            <p className="text-xs text-green-600 flex items-center gap-1">
                              <Truck className="w-3 h-3" />
                              Il cliente non pagherà spese di spedizione
                            </p>
                          )}
                        </div>

                        {/* Attachments Section */}
                        <div className="border border-slate-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Paperclip className="w-4 h-4" />
                              Allegati
                            </Label>
                            <span className="text-xs text-slate-500">
                              {proposalAttachments.length}/{MAX_ATTACHMENTS}
                            </span>
                          </div>
                          
                          <p className="text-xs text-slate-500 mb-3">
                            Allega immagini o documenti (JPG, PNG, WEBP, PDF - max 5MB)
                          </p>
                          
                          {/* Upload Button */}
                          {proposalAttachments.length < MAX_ATTACHMENTS && (
                            <div className="mb-3">
                              <label className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#008080] hover:bg-[#008080]/5 transition-colors">
                                {uploadingAttachment ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin text-[#008080]" />
                                    <span className="text-sm text-slate-600">Caricamento...</span>
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 text-[#008080]" />
                                    <span className="text-sm text-[#008080]">Carica file</span>
                                  </>
                                )}
                                <input
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                                  multiple
                                  onChange={handleAttachmentUpload}
                                  disabled={uploadingAttachment}
                                  className="hidden"
                                  data-testid="proposal-attachment-input"
                                />
                              </label>
                            </div>
                          )}
                          
                          {/* Attachments List */}
                          {proposalAttachments.length > 0 && (
                            <div className="space-y-2">
                              {proposalAttachments.map((attachment, index) => (
                                <div 
                                  key={index}
                                  className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg"
                                >
                                  {attachment.type === 'image' ? (
                                    <img 
                                      src={attachment.url} 
                                      alt={attachment.originalName}
                                      className="w-10 h-10 object-cover rounded"
                                    />
                                  ) : (
                                    <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                                      <FileText className="w-5 h-5 text-red-600" />
                                    </div>
                                  )}
                                  <span className="flex-1 text-xs text-slate-600 truncate">
                                    {attachment.originalName}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAttachment(index)}
                                    className="p-1 hover:bg-slate-200 rounded transition-colors"
                                    data-testid={`remove-attachment-${index}`}
                                  >
                                    <X className="w-4 h-4 text-slate-400" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {profile?.tier === 'free' && (
                          <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm">
                            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                            <div>
                              <p className="text-amber-700 font-medium">Piano Free</p>
                              <p className="text-amber-600 text-xs">5 candidature/settimana</p>
                            </div>
                          </div>
                        )}

                        <Button 
                          onClick={handleSubmitProposal}
                          disabled={submittingProposal || !proposalMessage.trim() || !proposalPrice}
                          className="w-full bg-[#DAA520] text-white hover:bg-[#B8860B] py-6"
                          data-testid="detail-send-proposal-btn"
                        >
                          {submittingProposal ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Invio...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5 mr-2" />
                              Invia Proposta
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </Card>
              ) : isOwnRequest ? (
                <Card className="p-6 text-center">
                  <User className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-semibold text-lg mb-2">Questa è la tua richiesta</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Vai alla dashboard per vedere le proposte ricevute.
                  </p>
                  <Link to="/dashboard">
                    <Button className="w-full bg-[#008080] text-white">
                      Vai alla Dashboard
                    </Button>
                  </Link>
                </Card>
              ) : (
                <Card className="p-6 text-center">
                  <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-semibold text-lg mb-2">Vuoi inviare una proposta?</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Accedi come artigiano per inviare la tua proposta a questo cliente.
                  </p>
                  <Link to="/login">
                    <Button className="w-full bg-[#008080] text-white">
                      Accedi
                    </Button>
                  </Link>
                </Card>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
